import { useContext, useEffect, useCallback } from 'react'
import { Popover } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { useSelector } from 'react-redux'
import { ApprovalDetails } from '@components'
import { submissionContext } from '@contexts'
import { getPusherInstance, CHANNELS } from '@global/js/pusher'
import { Submission, SubmissionSummary } from '@global/types'
import * as css from './styles'
import { phaseProgressMap, Phase, steps } from '../../config'
import { SubmissionStatusOutbound } from '../../submission-status-outbound'
import { useApprovalFlowDetails } from '../../utils'
import { SubmissionStatusRatio } from '../submissionStatusRatio'
import { SubmissionStatusStepper } from '../submissionStatusStepper'
import { t } from '@lingui/core/macro'
import { RootState } from '@redux/store'

const usePopoverStyles = makeStyles(() => ({
  paper: {
    pointerEvents: 'all',
    padding: 16,
  },
}))

interface Props {
  anchorEl: HTMLElement | null
  submission: Submission
  submissionSummary: SubmissionSummary
  open: boolean
  approvalFlowEnabled: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  onClick: (e) => void
}

export const SubmissionStatusPopover = ({
  submission,
  submissionSummary,
  open,
  approvalFlowEnabled,
  ...props
}: Props) => {
  const classes = usePopoverStyles()
  const { approvalLabel, approvalActionTimestamp } = useApprovalFlowDetails(
    approvalFlowEnabled,
    submission,
  )
  const { dataset_id, phase, submission_id } = submission
  const accountId = useSelector((state: RootState) => state.Auth.account.id)
  const { fetchSubmissionSummaries } = useContext(submissionContext)
  const Client = getPusherInstance()

  useEffect(() => {
    if (open) {
      subscribeToSummaryChanges()
    }
    return unsubscribeToSummaryChanges

    function subscribeToSummaryChanges() {
      const channelDefinition = CHANNELS.ACCOUNT_DATASET_POST_CHANGES
      const channel = Client.subscribe(channelDefinition.name(accountId, dataset_id))
      channel.bind(channelDefinition.events.EVENT_DATASET_POST_CHECKED, onDatasetPostChecked)
    }

    function unsubscribeToSummaryChanges() {
      const channelDefinition = CHANNELS.ACCOUNT_DATASET_POST_CHANGES
      const channelName = channelDefinition.name(accountId, dataset_id)
      const channel = Client.channel(channelName)
      if (channel) {
        Client.unsubscribe(channelName)
        channel.unbind(channelDefinition.events.EVENT_DATASET_POST_CHECKED, onDatasetPostChecked)
      }
    }
  }, [open])

  const onDatasetPostChecked = useCallback(() => {
    fetchSubmissionSummaries([submission_id])
  }, [submission_id])

  const hasOutbound = Boolean(submissionSummary?.datasetSubmissionId)
  const showOutbound =
    hasOutbound && (phase === Phase.WAITING_ON_OUTSTREAMS || phase === Phase.COMPLETED)

  const submissionSteps = steps()

  return (
    <Popover
      id="submission-status-popover"
      css={css.popover}
      classes={{
        paper: classes.paper,
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      disableRestoreFocus
      open={open}
      {...props}
    >
      <css.SectionHeader>{t`Claravine`}</css.SectionHeader>
      <css.PhasesContainer css={css.padding}>
        <SubmissionStatusRatio
          phase={phase}
          steps={submissionSteps}
          progressMap={phaseProgressMap}
        />
        <css.VerticalDivider />
        <SubmissionStatusStepper
          submission={submission}
          steps={submissionSteps}
          progressMap={phaseProgressMap}
        />
      </css.PhasesContainer>
      {Boolean(approvalActionTimestamp) && submission?.approval_result && (
        <css.ApprovalActionContainer>
          <ApprovalDetails approvalResult={submission?.approval_result} label={approvalLabel} />
        </css.ApprovalActionContainer>
      )}
      {showOutbound && (
        <>
          <css.HorizontalDivider />
          <css.SectionHeader>{t`Outbound destinations`}</css.SectionHeader>
          <css.OutboundContainer css={css.padding}>
            <SubmissionStatusOutbound
              submission={submission}
              submissionSummary={submissionSummary}
            />
          </css.OutboundContainer>
        </>
      )}
    </Popover>
  )
}
